/* eslint-disable @typescript-eslint/no-explicit-any */
import { upperCase } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';
import qs from 'qs';
import { paths } from '@common/types';
import { stableKey } from '@common/cache/util.ts';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const reportsRequest = async (
  props: paths[`/reporting/reports`]['get']['parameters']['query'],
) => {
  const response = axiosInstance.get<
    paths[`/reporting/reports`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/reports`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.sort_by && { sort_by: props.sort_by }),
      ...(props?.sort_order && {
        ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      }),
      ...(props?.search && { search: props.search }),
      ...(props?.organization_id && { organization_id: props.organization_id }),
      ...(props?.report_folder_id && {
        report_folder_id: props.report_folder_id,
      }),
      ...(props?.favorites_only && { favorites_only: props.favorites_only }),
      ...(props?.recently_viewed && { recently_viewed: props.recently_viewed }),
      ...(props?.created_by_me && { created_by_me: props.created_by_me }),
      ...(props?.report_type && { report_type: props.report_type }),
      ...(props?.recently_deleted && {
        recently_deleted: props.recently_deleted,
      }),
    },
  });
  return (await response).data;
};

export const useReportsQuery = (
  props: paths[`/reporting/reports`]['get']['parameters']['query'],
) =>
  useQuery({
    queryKey: ['reports', stableKey(props)],
    queryFn: () => reportsRequest(props),
  });

interface IReportProps {
  report_id?: string;
}

const reportRequest = async (props: IReportProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/reports/{report_id}`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/reports/${props.report_id}`, { params: { timezone } });
  return (await response).data;
};

export const useReportQuery = (props: IReportProps) =>
  useQuery({
    queryKey: ['report', props.report_id],
    enabled: !!props.report_id,
    queryFn: () => reportRequest(props),
  });

const reportFoldersRequest = async (
  props: paths[`/reporting/folders`]['get']['parameters']['query'],
) => {
  const response = axiosInstance.get<
    paths[`/reporting/folders`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/folders`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.sort_by && { sort_by: props.sort_by }),
      ...(props?.sort_order && {
        ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      }),
      ...(props?.search && { search: props.search }),
      ...(props?.organization_id && { organization_id: props.organization_id }),
      ...(props?.favorites_only && { favorites_only: props.favorites_only }),
      ...(props?.recently_viewed && { recently_viewed: props.recently_viewed }),
      ...(props?.created_by_me && { created_by_me: props.created_by_me }),
      ...(props?.recently_deleted && {
        recently_deleted: props.recently_deleted,
      }),
    },
  });
  return (await response).data;
};

export const useReportFoldersQuery = (
  props: paths[`/reporting/folders`]['get']['parameters']['query'],
) =>
  useQuery({
    queryKey: ['reportFolders', stableKey(props)],
    queryFn: () => reportFoldersRequest(props),
  });

const reportFolderRequest = async (report_folder_id: string) => {
  const response = axiosInstance.get<
    paths[`/reporting/folders/{report_folder_id}`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/folders/${report_folder_id}`, { params: { timezone } });
  return (await response).data;
};

export const useReportFolderQuery = (report_folder_id: string) =>
  useQuery({
    queryKey: ['reportFolder', report_folder_id],
    enabled: !!report_folder_id,
    queryFn: () => reportFolderRequest(report_folder_id),
  });

const foldersShortRequest = async () => {
  const response = axiosInstance.get<
    paths[`/reporting/folders/short`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/folders/short`, { params: { timezone } });
  return (await response).data;
};

export const useFoldersShortQuery = () =>
  useQuery({
    queryKey: ['foldersShort'],
    queryFn: () => foldersShortRequest(),
  });

const patientIntrospectionRequest = async () => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/reports/introspection`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/reports/introspection`, { params: { timezone } });
  return (await response).data;
};
export const usePatientIntrospectionQuery = () =>
  useQuery({
    queryKey: ['patientIntrospection'],
    queryFn: () => patientIntrospectionRequest(),
  });

const userIntrospectionRequest = async () => {
  const response = axiosInstance.get<
    paths[`/reporting/users/reports/introspection`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/reports/introspection`, { params: { timezone } });
  return (await response).data;
};
export const useUserIntrospectionQuery = () =>
  useQuery({
    queryKey: ['userIntrospection'],
    queryFn: () => userIntrospectionRequest(),
  });

const taskIntrospectionRequest = async () => {
  const response = axiosInstance.get<
    paths[`/reporting/tasks/reports/introspection`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/tasks/reports/introspection`, { params: { timezone } });
  return (await response).data;
};
export const useTaskIntrospectionQuery = () =>
  useQuery({
    queryKey: ['taskIntrospection'],
    queryFn: () => taskIntrospectionRequest(),
  });

const recordIntrospectionRequest = async () => {
  const response = axiosInstance.get<
    paths[`/reporting/record/reports/introspection`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/record/reports/introspection`, { params: { timezone } });
  return (await response).data;
};
export const useRecordIntrospectionQuery = () =>
  useQuery({
    queryKey: ['recordIntrospection'],
    queryFn: () => recordIntrospectionRequest(),
  });

type IPatientReportProps = Partial<
  paths[`/reporting/patients/reports/preview`]['get']['parameters']['query']
>;
type ITaskReportProps = Partial<
  paths[`/reporting/tasks/reports/preview`]['get']['parameters']['query']
>;
type IUserReportProps = Partial<
  paths[`/reporting/users/reports/preview`]['get']['parameters']['query']
>;
type IRecordReportProps = Partial<
  paths[`/reporting/record/reports/preview`]['get']['parameters']['query']
>;

// helper to clean $and entries from dynamic form submissions
export const cleanAnd = (props: { options?: any }) => {
  const filteredAnd = props.options?.$and?.filter(
    (entry: any) =>
      entry?.value !== undefined &&
      entry?.value !== null &&
      !(typeof entry.value === 'string' && entry.value.trim() === '') &&
      !(Array.isArray(entry.value) && entry.value.length === 0),
  );
  return { ...props.options, $and: filteredAnd };
};

const patientPreviewRequest = async (props: IPatientReportProps) => {
  const options = cleanAnd(props);
  const response = axiosInstance.get<
    paths[`/reporting/patients/reports/preview`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/reports/preview`, {
    params: {
      timezone,
      page: props.page,
      limit: props.limit,
      search: props.search,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.options !== undefined && { options }),
      ...(props.interval !== undefined && { interval: props.interval }),
      ...(props.custom_timeframe &&
        props.interval === 'custom' && {
          custom_timeframe: {
            ...(props.custom_timeframe.from_date && {
              from_date: props.custom_timeframe.from_date,
            }),
            ...(props.custom_timeframe.to_date && {
              to_date: props.custom_timeframe.to_date,
            }),
          },
        }),
    },
    paramsSerializer: {
      serialize: params =>
        qs.stringify(params, {
          arrayFormat: 'indices',
          encodeValuesOnly: true,
        }),
    },
  });
  return (await response).data;
};
export const usePatientPreviewQuery = ({
  enabled = true,
  ...props
}: IPatientReportProps & { enabled?: boolean }) =>
  useQuery({
    queryKey: ['patient', 'report', 'preview', stableKey(props)],
    queryFn: () => patientPreviewRequest(props),
    enabled,
  });

const taskPreviewRequest = async (props: ITaskReportProps) => {
  const options = cleanAnd(props);
  const response = axiosInstance.get<
    paths[`/reporting/tasks/reports/preview`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/tasks/reports/preview`, {
    params: {
      timezone,
      page: props.page,
      limit: props.limit,
      search: props.search,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.options !== undefined && { options }),
      ...(props.interval !== undefined && { interval: props.interval }),
      ...(props.custom_timeframe &&
        props.interval === 'custom' && {
          custom_timeframe: {
            ...(props.custom_timeframe.from_date && {
              from_date: props.custom_timeframe.from_date,
            }),
            ...(props.custom_timeframe.to_date && {
              to_date: props.custom_timeframe.to_date,
            }),
          },
        }),
    },
    paramsSerializer: {
      serialize: params =>
        qs.stringify(params, {
          arrayFormat: 'indices',
          encodeValuesOnly: true,
        }),
    },
  });
  return (await response).data;
};
export const useTaskPreviewQuery = ({
  enabled = true,
  ...props
}: ITaskReportProps & { enabled?: boolean }) =>
  useQuery({
    queryKey: ['task', 'report', 'preview', stableKey(props)],
    queryFn: () => taskPreviewRequest(props),
    enabled,
  });

const userPreviewRequest = async (props: IUserReportProps) => {
  const options = cleanAnd(props);
  const response = axiosInstance.get<
    paths[`/reporting/users/reports/preview`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/reports/preview`, {
    params: {
      timezone,
      page: props.page,
      limit: props.limit,
      search: props.search,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.options !== undefined && { options }),
      ...(props.interval !== undefined && { interval: props.interval }),
      ...(props.custom_timeframe &&
        props.interval === 'custom' && {
          custom_timeframe: {
            ...(props.custom_timeframe.from_date && {
              from_date: props.custom_timeframe.from_date,
            }),
            ...(props.custom_timeframe.to_date && {
              to_date: props.custom_timeframe.to_date,
            }),
          },
        }),
    },
    paramsSerializer: {
      serialize: params =>
        qs.stringify(params, {
          arrayFormat: 'indices',
          encodeValuesOnly: true,
        }),
    },
  });
  return (await response).data;
};
export const useUserPreviewQuery = ({
  enabled = true,
  ...props
}: IUserReportProps & { enabled?: boolean }) =>
  useQuery({
    queryKey: ['user', 'report', 'preview', stableKey(props)],
    queryFn: () => userPreviewRequest(props),
    enabled,
  });

const recordPreviewRequest = async (props: IRecordReportProps) => {
  const options = cleanAnd(props);
  const response = axiosInstance.get<
    paths[`/reporting/record/reports/preview`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/record/reports/preview`, {
    params: {
      timezone,
      page: props.page,
      limit: props.limit,
      search: props.search,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.options !== undefined && { options }),
      ...(props.interval !== undefined && { interval: props.interval }),
      ...(props.custom_timeframe &&
        props.interval === 'custom' && {
          custom_timeframe: {
            ...(props.custom_timeframe.from_date && {
              from_date: props.custom_timeframe.from_date,
            }),
            ...(props.custom_timeframe.to_date && {
              to_date: props.custom_timeframe.to_date,
            }),
          },
        }),
    },
    paramsSerializer: {
      serialize: params =>
        qs.stringify(params, {
          arrayFormat: 'indices',
          encodeValuesOnly: true,
        }),
    },
  });
  return (await response).data;
};
export const useRecordPreviewQuery = ({
  enabled = true,
  ...props
}: IRecordReportProps & { enabled?: boolean }) =>
  useQuery({
    queryKey: ['record', 'report', 'preview', stableKey(props)],
    queryFn: () => recordPreviewRequest(props),
    enabled,
  });

const patientsReportItemsRequest = async (
  report_id: string,
  props: paths[`/reporting/patients/reports/{report_id}/items`]['get']['parameters']['query'],
) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/reports/{report_id}/items`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/reports/${report_id}/items`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.search && { search: props.search }),
      ...(props?.sort_by && { sort_by: props.sort_by }),
      ...(props?.sort_order && {
        ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      }),
      ...(props?.$and && { $and: props.$and }),
    },
  });
  return (await response).data;
};
export const usePatientReportItemsQuery = (
  report_id: string,
  props: paths[`/reporting/patients/reports/{report_id}/items`]['get']['parameters']['query'],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ['patientReportItems', report_id, stableKey(props)],
    enabled: enabled && !!report_id,
    queryFn: () => patientsReportItemsRequest(report_id, props),
  });

const usersReportItemsRequest = async (
  report_id: string,
  props: paths[`/reporting/users/reports/{report_id}/items`]['get']['parameters']['query'],
) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/reports/{report_id}/items`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/reports/${report_id}/items`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.search && { search: props.search }),
      ...(props?.sort_by && { sort_by: props.sort_by }),
      ...(props?.sort_order && {
        ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      }),
      ...(props?.$and && { $and: props.$and }),
    },
  });
  return (await response).data;
};
export const useUserReportItemsQuery = (
  report_id: string,
  props: paths[`/reporting/users/reports/{report_id}/items`]['get']['parameters']['query'],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ['userReportItems', stableKey(props)],
    enabled: enabled && !!report_id,
    queryFn: () => usersReportItemsRequest(report_id, props),
  });

const tasksReportItemsRequest = async (
  report_id: string,
  props: paths[`/reporting/tasks/reports/{report_id}/items`]['get']['parameters']['query'],
) => {
  const response = axiosInstance.get<
    paths[`/reporting/tasks/reports/{report_id}/items`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/tasks/reports/${report_id}/items`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.search && { search: props.search }),
      ...(props?.sort_by && { sort_by: props.sort_by }),
      ...(props?.sort_order && {
        ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      }),
      ...(props?.$and && { $and: props.$and }),
    },
  });
  return (await response).data;
};
export const useTaskReportItemsQuery = (
  report_id: string,
  props: paths[`/reporting/tasks/reports/{report_id}/items`]['get']['parameters']['query'],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ['taskReportItems', report_id, stableKey(props)],
    enabled: enabled && !!report_id,
    queryFn: () => tasksReportItemsRequest(report_id, props),
  });

const recordsReportItemsRequest = async (
  report_id: string,
  props: paths[`/reporting/record/reports/{report_id}/items`]['get']['parameters']['query'],
) => {
  const response = axiosInstance.get<
    paths[`/reporting/record/reports/{report_id}/items`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/record/reports/${report_id}/items`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.search && { search: props.search }),
      ...(props?.sort_by && { sort_by: props.sort_by }),
      ...(props?.sort_order && {
        ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      }),
      ...(props?.$and && { $and: props.$and }),
    },
  });
  return (await response).data;
};
export const useRecordReportItemsQuery = (
  report_id: string,
  props: paths[`/reporting/record/reports/{report_id}/items`]['get']['parameters']['query'],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ['recordReportItems', stableKey(props)],
    enabled: enabled && !!report_id,
    queryFn: () => recordsReportItemsRequest(report_id, props),
  });

const patientReportGroupBySectionsRequest = async (
  report_id: string,
  props: paths[`/reporting/patients/reports/{report_id}/groups`]['get']['parameters']['query'],
) => {
  const options = cleanAnd({ options: { $and: props.$and } });
  const response = axiosInstance.get<
    paths[`/reporting/patients/reports/{report_id}/groups`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/patients/reports/${report_id}/groups`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.search && { search: props.search }),
      ...(props?.group_by && { group_by: props.group_by }),
      ...(options?.$and && options?.$and.length > 0 && { $and: options.$and }),
    },
  });
  return (await response).data;
};
export const usePatientReportGroupBySectionsQuery = (
  report_id: string,
  props: paths[`/reporting/patients/reports/{report_id}/groups`]['get']['parameters']['query'],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ['patientReportGroups', report_id, stableKey(props)],
    enabled: enabled && !!report_id,
    queryFn: () => patientReportGroupBySectionsRequest(report_id, props),
  });

const userReportGroupBySectionsRequest = async (
  report_id: string,
  props: paths[`/reporting/users/reports/{report_id}/groups`]['get']['parameters']['query'],
) => {
  const response = axiosInstance.get<
    paths[`/reporting/users/reports/{report_id}/groups`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/users/reports/${report_id}/groups`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.search && { search: props.search }),
      ...(props?.group_by && { group_by: props.group_by }),
      ...(props?.$and && props.$and.length > 0 && { $and: props.$and }),
    },
  });
  return (await response).data;
};
export const useUserReportGroupBySectionsQuery = (
  report_id: string,
  props: paths[`/reporting/users/reports/{report_id}/groups`]['get']['parameters']['query'],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ['userReportGroups', report_id, stableKey(props)],
    enabled: enabled && !!report_id,
    queryFn: () => userReportGroupBySectionsRequest(report_id, props),
  });

const taskReportGroupBySectionsRequest = async (
  report_id: string,
  props: paths[`/reporting/tasks/reports/{report_id}/groups`]['get']['parameters']['query'],
) => {
  const options = cleanAnd({ options: { $and: props.$and } });
  const response = axiosInstance.get<
    paths[`/reporting/tasks/reports/{report_id}/groups`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/tasks/reports/${report_id}/groups`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.search && { search: props.search }),
      ...(props?.group_by && { group_by: props.group_by }),
      ...(options.$and && options.$and.length > 0 && { $and: options.$and }),
    },
  });
  return (await response).data;
};
export const useTaskReportGroupBySectionsQuery = (
  report_id: string,
  props: paths[`/reporting/tasks/reports/{report_id}/groups`]['get']['parameters']['query'],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ['taskReportGroups', report_id, stableKey(props)],
    enabled: enabled && !!report_id,
    queryFn: () => taskReportGroupBySectionsRequest(report_id, props),
  });

const recordReportGroupBySectionsRequest = async (
  report_id: string,
  props: paths[`/reporting/record/reports/{report_id}/groups`]['get']['parameters']['query'],
) => {
  const options = cleanAnd({ options: { $and: props.$and } });
  const response = axiosInstance.get<
    paths[`/reporting/record/reports/{report_id}/groups`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/record/reports/${report_id}/groups`, {
    params: {
      timezone,
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.search && { search: props.search }),
      ...(props?.group_by && { group_by: props.group_by }),
      ...(options?.$and && options?.$and.length > 0 && { $and: options.$and }),
    },
  });
  return (await response).data;
};
export const useRecordReportGroupBySectionsQuery = (
  report_id: string,
  props: paths[`/reporting/record/reports/{report_id}/groups`]['get']['parameters']['query'],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ['recordReportGroups', report_id, stableKey(props)],
    enabled: enabled && !!report_id,
    queryFn: () => recordReportGroupBySectionsRequest(report_id, props),
  });

///////// FILTER VALUES ////////////////

type ReportSegment = 'patients' | 'users' | 'tasks' | 'record';

interface IFilterValuesProps {
  segment: ReportSegment;
  field: string;
  search?: string;
}

const filterValuesRequest = async ({
  segment,
  field,
  search,
}: IFilterValuesProps) => {
  const response = axiosInstance.get<
    paths[`/reporting/patients/reports/filter-values`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/${segment}/reports/filter-values`, {
    params: {
      timezone,
      field,
      ...(search && { search }),
    },
  });
  return (await response).data;
};

export const useFilterValuesQuery = ({
  enabled = true,
  ...props
}: IFilterValuesProps & { enabled?: boolean }) =>
  useQuery({
    queryKey: ['reportFilterValues', stableKey(props)],
    enabled: enabled && !!props.field,
    queryFn: () => filterValuesRequest(props),
  });

///////// REPORT HISTORY ////////////////

const reportHistoryRequest = async (report_id: string, page: number) => {
  const response = axiosInstance.get<
    paths[`/reporting/reports/{report_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/reporting/reports/${report_id}/history`, { params: { timezone, page } });
  return (await response).data;
};

export const useReportHistoryQuery = ({
  report_id,
  enabled,
  page,
}: {
  report_id: string;
  enabled: boolean;
  page: number;
}) =>
  useQuery({
    queryKey: ['report', report_id, 'history', page],
    enabled: !!report_id && enabled,
    queryFn: () => reportHistoryRequest(report_id, page),
  });
